import { showMessage } from '@/components/antd-comp/message'
import HighlighterCom from '@/components/CanvasHighlighter/HighlighterCom'
import MarkdownViewer from '@/components/file-preview/markdown'
import OfficePreview from '@/components/file-preview/office'
import PictureViewer from '@/components/file-preview/picture'
import TextFileViewer from '@/components/file-preview/text/index'
import { locationContext } from '@/contexts/locationContext'
import { customFetch } from '@/controllers/request'
import { useBuriedEvent } from '@/util/buried'
import { LoadingOutlined } from '@ant-design/icons'
import { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import * as streamSaver from 'streamsaver'
import { FileInfoType } from '../constants'
import '../css/preview.less'

const getFileName = url => {
  const parsedUrl = new URL(url)
  const pathParts = parsedUrl.pathname.split('/')
  return pathParts[pathParts.length - 1].replace(/\/$/, '')
}

const downloadFile = async (fileUrl, downloadName, setIsDownload) => {
  console.log('fileUrl, downloadName, setIsDownload', fileUrl, downloadName, setIsDownload)
  setIsDownload(true)
  // 走后端
  // const response = await fetch(`/api/v1/knowledge/download?file_path=${fileUrl}`)
  // 直接走sso

  const response = await customFetch(fileUrl, undefined, true)
  try {
    console.log('开始文件下载失败', response)
    // 检查HTTP状态码是否在200-299之间
    if (!response.ok) {
      showMessage({
        content: `${response.status} 文件下载失败`,
        type: 'error'
      })
      return
    }
    const fileStream = streamSaver.createWriteStream(downloadName)
    const writer = fileStream.getWriter()

    const reader = response.body.getReader()
    const pump = () =>
      reader.read().then(({ value, done }) => {
        if (done) writer.close()
        else {
          writer.write(value)
          return writer.ready.then(pump)
        }
      })

    await pump()
      .then(() => {
        console.log('Closed the stream, Done writing')
        setTimeout(() => {
          setIsDownload(false)
        }, 1000)
      })
      .catch(err => console.log(err))
  } catch (error) {
    showMessage({
      content: error,
      type: 'error'
    })
    setTimeout(() => {
      setIsDownload(false)
    }, 1000)
  }
}
/**
 * 文档预览入口
 */
interface PreviewPageProps {
  fileInfo: FileInfoType
  open?: boolean
  setOpen: (status: boolean) => void
  knowledgeTraceInfo?: any // 从PopoverCont传递过来的知识库溯源信息
}

const officeTypes = ['doc', 'docx', 'pdf', 'xls', 'xlsx', 'csv', 'ppt', 'pptx']
const textTypes = ['txt', 'log', 'json']
const pictureTypes = ['png', 'jpg', 'jpeg']
const markdownTypes = ['md']
const videoTypes = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'wmv', 'flv', 'mkv']
/* 溯源命中类型 */
const chunkType = ['chunk_pdf']

const PreviewPage = ({ fileInfo, setOpen, knowledgeTraceInfo }: PreviewPageProps) => {
  const { id } = useParams()
  const { appConfig } = useContext(locationContext)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [filetype, setFileType] = useState('txt')
  const [renderedComponent, setRenderedComponent] = useState(<></>)
  const handleError = msg => {
    showMessage({
      content: msg || '文件加载失败',
      type: 'error'
    })
    setLoading(false)
    setLoadError(true)
  }

  // ESC退出
  useEffect(() => {
    const handleEscKey = event => {
      if (event.key === 'Escape') {
        console.log('Escape key was pressed!')
        setOpen(false)
      }
    }
    window.addEventListener('keydown', handleEscKey)

    console.log('>>> OfficePreview fileInfo: ', fileInfo)
    return () => {
      window.removeEventListener('keydown', handleEscKey)
    }
  }, [])

  useEffect(() => {
    const type = fileInfo?.file_name ? fileInfo.file_name.slice(fileInfo.file_name.lastIndexOf('.') + 1) : ''
    console.log('PreviewPage type', type)
    let component = <></>
    if (textTypes.includes(type)) {
      component = (
        <div className="text-preview-box">
          <div className="text-preview">
            <TextFileViewer
              configProps={{
                serverUrl: appConfig.officeUrl,
                key: fileInfo.file_name,
                fileType: type,
                title: fileInfo.file_name,
                fileUrl: fileInfo.file_path
                // fileUrl: getFileName(fileInfo.file_path)
              }}
              setLoading={setLoading}
              // downloadFile={() => downloadFile(getFileName(fileInfo.file_path), fileInfo.file_name, setIsDownload)}
              // minio迁移到sso
              downloadFile={() => downloadFile(fileInfo.file_path, fileInfo.file_name, setIsDownload)}
              handleError={handleError}
            />
          </div>
        </div>
      )
    } else if (pictureTypes.includes(type)) {
      component = (
        <div className="picture-preview">
          <PictureViewer
            configProps={{
              serverUrl: appConfig.officeUrl,
              key: fileInfo.file_name,
              fileType: type,
              title: fileInfo.file_name,
              fileUrl: fileInfo.file_path
              // fileUrl: getFileName(fileInfo.file_path)
            }}
            setLoading={setLoading}
            handleError={handleError}
          />
        </div>
      )
    } else if (markdownTypes.includes(type)) {
      component = (
        <div className="text-preview-box">
          <div className="text-preview">
            <MarkdownViewer
              configProps={{
                serverUrl: appConfig.officeUrl,
                key: fileInfo.file_name,
                fileType: type,
                title: fileInfo.file_name,
                fileUrl: fileInfo.file_path
                // fileUrl: getFileName(fileInfo.file_path)
              }}
              setLoading={setLoading}
              // downloadFile={() => downloadFile(getFileName(fileInfo.file_path), fileInfo.file_name, setIsDownload)}
              downloadFile={() => downloadFile(fileInfo.file_path, fileInfo.file_name, setIsDownload)}
              handleError={handleError}
            />
          </div>
        </div>
      )
    } else if (officeTypes.includes(type)) {
      component = (
        <div className="office-preview">
          <OfficePreview
            configProps={{
              serverUrl: appConfig.officeUrl,
              key: getFileName(fileInfo.file_path),
              fileType: type,
              title: getFileName(fileInfo.file_path),
              fileUrl: fileInfo.file_path
            }}
            setLoading={setLoading}
            handleError={handleError}
          />
        </div>
      )
    } else if (chunkType.includes(type)) {
      component = (
        <div className="chunkType-preview">
          <div className="caseOverFlowY ml-auto mr-auto w-[50%]">
            <HighlighterCom
              setLoading={setLoading}
              chunk_id={fileInfo?.id} // chunk_id
              knowledgeTraceInfo={knowledgeTraceInfo} // 传递知识库溯源信息
            ></HighlighterCom>
          </div>
        </div>
      )
    } else if (videoTypes.includes(type)) {
      component = (
        <div className="video-preview">
          <video
            src={fileInfo.file_path}
            controls
            autoPlay
            className="video-preview-player"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
            onError={e => {
              const videoElement = e.target as HTMLVideoElement
              const error = videoElement.error
              console.error('视频加载失败:', {
                error,
                code: error?.code,
                message: error?.message,
                videoUrl: fileInfo.file_path,
                videoType: type
              })

              let errorMessage = '视频加载失败'
              if (error) {
                switch (error.code) {
                  case 1: // MEDIA_ERR_ABORTED
                    errorMessage = '视频加载被中止'
                    break
                  case 2: // MEDIA_ERR_NETWORK
                    errorMessage = '网络错误，无法加载视频'
                    break
                  case 3: // MEDIA_ERR_DECODE
                    errorMessage = '视频解码失败，可能是不支持的格式'
                    break
                  case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
                    errorMessage = '浏览器不支持此视频格式'
                    break
                }
              }
              handleError(errorMessage)
            }}
            onLoadedData={() => {
              console.log('视频加载成功')
              setLoading(false)
            }}
            onLoadStart={() => {
              console.log('开始加载视频:', fileInfo.file_path)
            }}
            onCanPlay={() => {
              console.log('视频可以播放')
              setLoading(false)
            }}
          />
        </div>
      )
      // 延迟设置 loading 为 false，等待视频开始加载
      setTimeout(() => {
        setLoading(false)
      }, 500)
    } else {
      component = (
        <div className="text-preview-box">
          <div className="min-h-[calc(100vh-144px)]">尚为支持的文件类型</div>
        </div>
      )
    }
    setRenderedComponent(component)
    setFileType(type)
  }, [fileInfo])

  const [isDownload, setIsDownload] = useState(false)
  const { fileLibBuriedEvent } = useBuriedEvent()

  const closeFun = () => {
    fileLibBuriedEvent({
      page: '文件预览页面',
      event_name: '【关闭】按钮',
      data_id: id
    })
    setOpen(false)
  }

  // 判断是否为溯源切片
  const chunkTypeFun = () => {
    const fileName = fileInfo?.file_name
    if (!fileName) return false
    const ext = fileName?.slice(fileName.lastIndexOf('.') + 1)
    if (ext === 'chunk_pdf') {
      return true
    }
    return false
  }

  // 获取交集
  const intersection =
    knowledgeTraceInfo?.badges?.filter(item =>
      knowledgeTraceInfo?.cur_trace_file_info?.chunk_index_list?.includes(item)
    ) ?? []

  return (
    <div className="lenovo-file-preview">
      <div className="top-preview">
        <div className="title">
          {/* 溯源单独判断名称显示  */}
          {chunkTypeFun() ? fileInfo.object_name : fileInfo.file_name}
          {knowledgeTraceInfo?.allHighlights && <span className="text-sm"> (共{intersection.length}个溯源)</span>}
        </div>
        <div className="buttons">
          {/* 下载按钮 - 支持知识库溯源 */}
          {isDownload ? (
            <LoadingOutlined style={{ color: 'white', width: '18px', height: '18px' }} />
          ) : (
            <span
              className="cursor-pointer"
              onClick={() => {
                fileLibBuriedEvent({
                  page: '文件预览页面',
                  event_name: '【下载】按钮',
                  data_id: id
                })

                const downloadUrl = fileInfo.file_path
                const downloadName = chunkTypeFun() ? fileInfo?.object_name : fileInfo.file_name

                if (downloadUrl && downloadName) {
                  downloadFile(downloadUrl, downloadName, setIsDownload)
                } else {
                  showMessage({
                    content: '下载链接不可用',
                    type: 'error'
                  })
                }
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                <path d="M16 9L16 19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path
                  d="M11 14L16 19L21 14"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path d="M10 23H22" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          )}

          <span
            className="cursor-pointer"
            onClick={() => {
              closeFun()
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 3L13 13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M3 13L13 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>
      </div>
      {!loading && !loadError && officeTypes.includes(filetype) && (
        <div
          className="office-left-mask"
          style={{ backgroundColor: ['csv', 'xls', 'xlsx'].includes(filetype) ? '#333333' : '#555555' }}
        ></div>
      )}

      {loading && (
        <div className="absolute left-0 top-0 z-10 flex h-full w-full items-center justify-center bg-[rgba(255,255,255,1)] dark:bg-blur-shared">
          <span className="loading loading-infinity loading-lg"></span>
        </div>
      )}
      {!loadError && renderedComponent}
    </div>
  )
}

export default PreviewPage
